import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Clock3, ArrowRight } from 'lucide-react';

export function cutoffWindows(plans = []) {
  return plans.filter(plan => !plan.is_meal_addon && !plan.is_delivery_addon && !plan.is_system && plan.is_active !== false)
    .map(plan => ({ ...(plan.next_order_delivery || plan.next_pack_delivery), plan_id: plan.id, plan_name: plan.alias || plan.name }))
    .filter(window => window.date && Number.isFinite(Date.parse(window.cutoff_at)))
    .sort((a, b) => Date.parse(a.cutoff_at) - Date.parse(b.cutoff_at));
}

export function themeCutoffPlansHref(website, homeUrl) {
  const setting = website?.package?.manifest?.settings?.cutoff_browse_plans;
  const enabled = website?.edits?.settings?.cutoff_browse_plans ?? setting?.default;
  return setting?.type === 'boolean' && enabled === true && Object.prototype.hasOwnProperty.call(website?.package?.pages || {}, 'plans')
    ? `${homeUrl}#plans` : undefined;
}

export default function OrderCutoff({ plans, onChoose, onRefresh, browsePlansHref }) {
  const [now, setNow] = useState(Date.now);
  const windows = useMemo(() => cutoffWindows(plans), [plans]);
  const refreshed = useRef(null);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;
  useEffect(() => {
    if (!windows.length) return undefined;
    const tick = () => setNow(Date.now());
    const timer = setInterval(tick, 1000);
    window.addEventListener('focus', tick);
    return () => { clearInterval(timer); window.removeEventListener('focus', tick); };
  }, [windows.length]);
  const expired = windows.find(window => Date.parse(window.cutoff_at) <= now);
  useEffect(() => {
    if (expired && refreshed.current !== expired.cutoff_at) {
      refreshed.current = expired.cutoff_at;
      refresh.current?.();
    }
  }, [expired]);
  const next = windows.find(window => Date.parse(window.cutoff_at) > now);
  if (!windows.length) return null;
  if (!next) return <div className="s6-cutoff"><span role="status">This ordering window has closed.</span><button type="button" className="s6-cutoff-action" onClick={() => onRefresh?.()}>Check next delivery <ArrowRight size={15} /></button></div>;
  const remaining = Math.max(0, Math.ceil((Date.parse(next.cutoff_at) - now) / 1000));
  const days = Math.floor(remaining / 86400);
  const hours = Math.floor(remaining / 3600) % 24;
  const minutes = Math.floor(remaining / 60) % 60;
  const seconds = remaining % 60;
  const dayLabel = new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(new Date(`${next.date}T12:00:00Z`));
  const cutoffLabel = new Intl.DateTimeFormat('en', { weekday: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: next.timezone || 'UTC' }).format(new Date(next.cutoff_at));
  return <aside className="s6-cutoff" data-testid="storefront-order-cutoff" aria-label="Order cutoff">
    <div className="s6-cutoff-copy"><Clock3 size={19} aria-hidden="true" /><div><strong>Order for {dayLabel}</strong><span>{next.plan_name} · Order before {cutoffLabel}</span></div></div>
    <div className="s6-countdown" role="timer" aria-label="Time remaining to order" aria-live="off">
      {[[days, 'days'], [hours, 'hrs'], [minutes, 'min'], [seconds, 'sec']].map(([value, label]) => <span key={label}><b>{String(value).padStart(2, '0')}</b><small>{label}</small></span>)}
    </div>
    <>{browsePlansHref ? <a className="s6-cutoff-action" href={browsePlansHref}>Choose meals <ArrowRight size={15} /></a> : <button type="button" className="s6-cutoff-action" onClick={() => onChoose?.(next.plan_id)}>Choose meals <ArrowRight size={15} /></button>}</>
  </aside>;
}
